"use client";

import { CalculationResult, formatPLN } from "@/lib/api";
import { exportResultsToXlsx } from "@/lib/export";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface ResultsViewProps {
  results: CalculationResult[];
  portalName: string;
}

function StatCard({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`
      p-4 rounded-xl transition-smooth
      ${accent ? "glass glow-blue" : "bg-blue-500/8 border border-blue-500/15"}
      hover:bg-blue-500/15
    `}
    >
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p
        className={`text-lg font-bold font-mono ${accent ? "gradient-text" : "text-blue-100"}`}
      >
        {value}
      </p>
    </div>
  );
}

export function ResultsView({ results, portalName }: ResultsViewProps) {
  if (results.length === 0) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold gradient-text">Wyniki obliczen</h2>
        <button
          onClick={() => exportResultsToXlsx(results, portalName)}
          className="px-5 py-2.5 rounded-xl font-semibold text-sm glass glass-hover text-blue-300 hover:text-white transition-smooth hover:scale-105 active:scale-95 flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Pobierz raport
        </button>
      </div>

      {results.map((result) => (
        <Card
          key={result.contract_number}
          className="glass border-blue-500/15 glow-blue-hover transition-smooth rounded-2xl overflow-hidden"
        >
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <CardTitle className="text-blue-100">
                  {result.registration_number || result.contract_number}
                </CardTitle>
                <Badge
                  variant="outline"
                  className="border-blue-500/30 text-blue-300"
                >
                  {result.contract_number}
                </Badge>
                {result.contract_date && (
                  <span className="text-sm text-muted-foreground">
                    {result.contract_date}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {result.invoice_number && (
                  <Badge
                    variant="outline"
                    className="border-blue-500/30 text-blue-300"
                  >
                    {result.invoice_number}
                  </Badge>
                )}
                {result.exceeds_limit && (
                  <Badge className="bg-amber-600/20 text-amber-400 border border-amber-500/30 hover:bg-amber-600/30">
                    Ponad 100 000 PLN
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Basic values */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                label="Wartość netto umowy"
                value={formatPLN(result.contract_value)}
              />
              <StatCard
                label="Wartość samochodu"
                value={formatPLN(result.car_value)}
                accent
              />
              <StatCard
                label="Współczynnik %"
                value={`${result.percentage_coefficient}%`}
              />
              <StatCard
                label="Brutto faktury"
                value={formatPLN(result.invoice.gross_value)}
              />
            </div>

            {/* Invoice details */}
            <div>
              <h3 className="text-sm font-medium text-blue-300 mb-3">
                Dane z faktury
              </h3>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                <StatCard
                  label="Kapitał"
                  value={formatPLN(result.invoice.capital)}
                />
                <StatCard
                  label="Odsetki"
                  value={formatPLN(result.invoice.interest)}
                />
                <StatCard
                  label="Dodatki"
                  value={formatPLN(result.invoice.additions)}
                />
                <StatCard
                  label="Netto"
                  value={formatPLN(result.invoice.net_value)}
                />
                <StatCard
                  label="VAT"
                  value={formatPLN(result.invoice.vat_value)}
                />
                <StatCard
                  label="Brutto"
                  value={formatPLN(result.invoice.gross_value)}
                />
              </div>
            </div>

            {/* Konto 249, 751, Dodatki - for all cars */}
            <div>
              <h3 className="text-sm font-medium text-blue-300 mb-3">
                Konta ksiegowe
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                  label={!result.is_vehicle ? "Konto 249 (100% VAT + kapitał)" : "Konto 249 (50% VAT + kapitał)"}
                  value={formatPLN(result.konto_249)}
                  accent
                />
                <StatCard
                  label="Konto 751 (odsetki)"
                  value={formatPLN(result.konto_751)}
                  accent
                />
                <StatCard
                  label="Dodatki"
                  value={formatPLN(result.additions)}
                />
              </div>
            </div>

            {/* KUP - pojazdy > 100k */}
            {result.exceeds_limit && result.kup_tax !== null && (
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-5">
                <h3 className="text-sm font-medium text-amber-400 mb-4">
                  KUP podatkowo (samochod &gt; 100 000 PLN)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="md:col-span-1">
                    <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                      <p className="text-xs text-amber-400/70 mb-1">KUP Suma</p>
                      <p className="text-xl font-bold font-mono text-amber-300">
                        {formatPLN(result.kup_tax!)}
                      </p>
                    </div>
                  </div>
                  <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard
                      label="Wsp. % z kapitału"
                      value={formatPLN(result.kup_capital_coefficient!)}
                    />
                    <StatCard
                      label="50% VAT od odl. kapitału"
                      value={formatPLN(result.kup_50_vat_capital!)}
                    />
                    <StatCard
                      label="Odsetki"
                      value={formatPLN(result.kup_interest!)}
                    />
                    <StatCard
                      label="50% VAT od odl. odsetek"
                      value={formatPLN(result.kup_50_vat_interest!)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* KUP - nie-pojazdy (np. wózek widłowy, maszyny) */}
            {!result.is_vehicle && result.kup_tax !== null && (
              <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-5">
                <h3 className="text-sm font-medium text-green-400 mb-4">
                  KUP podatkowo
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="md:col-span-1">
                    <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                      <p className="text-xs text-green-400/70 mb-1">KUP Suma</p>
                      <p className="text-xl font-bold font-mono text-green-300">
                        {formatPLN(result.kup_tax!)}
                      </p>
                    </div>
                  </div>
                  <div className="md:col-span-4 grid grid-cols-2 gap-3">
                    <StatCard
                      label="Część kapitałowa"
                      value={formatPLN(result.kup_capital_coefficient!)}
                    />
                    <StatCard
                      label="Część odsetkowa"
                      value={formatPLN(result.kup_interest!)}
                    />
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
